import React from 'react';
import { Bracket } from 'react-tournament-bracket';

// ── Palette ───────────────────────────────────────────────────────────────────
var PALETTE = ['#7c3aed','#2563eb','#059669','#d97706','#db2777','#0891b2','#9333ea','#65a30d'];

function hashColor(name) {
  var h = 0;
  for (var i = 0; i < (name || '').length; i++) { h = (name.charCodeAt(i) + ((h << 5) - h)); }
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function trunc(str, max) {
  str = (str === null || str === undefined) ? 'TBD' : String(str);
  return str.length > max ? str.substring(0, max - 1) + '\u2026' : str;
}

// ── Build game tree from flat fixture list ────────────────────────────────────
function buildGameTree(fixturesByLevel, levels, levelIdx, matchIdx) {
  if (levelIdx < 0 || levelIdx >= levels.length) return null;
  var level   = levels[levelIdx];
  var matches = fixturesByLevel[level] || [];
  if (matchIdx >= matches.length) return null;

  var entry    = matches[matchIdx];
  var fixture  = entry.fixture;
  var isFirst  = levelIdx === 0;

  var homeSource    = isFirst ? null : buildGameTree(fixturesByLevel, levels, levelIdx - 1, matchIdx * 2);
  var visitorSource = isFirst ? null : buildGameTree(fixturesByLevel, levels, levelIdx - 1, matchIdx * 2 + 1);

  var t1 = fixture.team_one;
  var t2 = fixture.team_two;
  var s1 = fixture.team1_scores && fixture.team1_scores.length ? fixture.team1_scores[0] : null;
  var s2 = fixture.team2_scores && fixture.team2_scores.length ? fixture.team2_scores[0] : null;

  return {
    id:       String(fixture.id || (level + '-' + matchIdx)),
    name:     level,
    _origIdx: entry.origIdx,
    _time:    fixture.session_start || null,
    sides: {
      home: {
        seed: {
          id:          String(t1.id || 'h-' + matchIdx),
          displayName: t1.name || 'TBD',
          rank:        1,
          wins:        t1.winner ? 1 : 0,
          sourceGame:  homeSource,
        },
        score: s1 !== null ? { score: s1 } : {},
      },
      visitor: {
        seed: {
          id:          String(t2.id || 'v-' + matchIdx),
          displayName: t2.name || 'TBD',
          rank:        1,
          wins:        t2.winner ? 1 : 0,
          sourceGame:  visitorSource,
        },
        score: s2 !== null ? { score: s2 } : {},
      },
    },
  };
}

// ── SVG Game Card ─────────────────────────────────────────────────────────────
// Renders inside the package's SVG canvas at position (x, y).
// Receives all props from Bracket including any extras in 'rest'.

function GameCard(props) {
  var game         = props.game;
  var x            = props.x     || 0;
  var y            = props.y     || 0;
  var W            = props.width  || 220;
  var H            = props.height || 100;
  var onMatchClick = props.onMatchClick;

  var home    = game.sides.home;
  var visitor = game.sides.visitor;

  var homeWins    = home.seed    && home.seed.wins    > 0;
  var visitorWins = visitor.seed && visitor.seed.wins > 0;
  var hasWinner   = homeWins || visitorWins;

  var homeName    = trunc(home.seed    ? home.seed.displayName    : 'TBD', 19);
  var visitorName = trunc(visitor.seed ? visitor.seed.displayName : 'TBD', 19);

  var isBye = homeName === 'BYE' || visitorName === 'BYE';
  var isTbd = homeName === 'TBD' && visitorName === 'TBD';

  // ── Color tokens ─────────────────────────────────────────────────────────
  var purple      = '#7c3aed';
  var purpleSoft  = '#ede9fe';
  var purpleDeep  = '#5b21b6';
  var borderCol   = hasWinner ? '#c4b5fd' : '#e5e7eb';
  var textDark    = '#111827';
  var textMuted   = '#9ca3af';
  var textLight   = '#d1d5db';
  var dividerCol  = '#f3f4f6';
  var homeColor   = hashColor(homeName);
  var visitorColor= hashColor(visitorName);
  var cardOpacity = (isBye || isTbd) ? 0.5 : 1;

  // ── Layout geometry ───────────────────────────────────────────────────────
  var timeBarH   = game._time ? 18 : 0;
  var bodyH      = H - timeBarH;
  var halfBodyH  = bodyH / 2;
  var dividerY   = y + timeBarH + halfBodyH;
  var homeRowMid = y + timeBarH + halfBodyH / 2;
  var vivRowMid  = y + timeBarH + halfBodyH + halfBodyH / 2;
  var avatarR    = 9;
  var avatarX    = x + 20;
  var nameX      = x + 36;
  var scoreX     = x + W - 10;
  var fs         = 11;
  var fsScore    = 13;

  var ff = "'DM Sans','Segoe UI',Arial,sans-serif";

  return (
    <g
      style={{ cursor: (isBye || isTbd) ? 'default' : 'pointer', opacity: cardOpacity }}
      onClick={function() { if (onMatchClick && !isBye) onMatchClick(game); }}
    >
      {/* ── Drop shadow ─────────────────────────────────────────────────── */}
      <rect x={x + 1} y={y + 3} width={W} height={H} rx={12} fill="rgba(0,0,0,0.06)" />

      {/* ── Card backdrop ───────────────────────────────────────────────── */}
      <rect x={x} y={y} width={W} height={H} rx={12}
        fill="#ffffff" stroke={borderCol} strokeWidth={1.5}
      />

      {/* ── Left accent strip (winner side) ─────────────────────────────── */}
      {homeWins && (
        <rect x={x} y={y} width={4} height={halfBodyH + timeBarH} rx={2} fill={purple} />
      )}
      {visitorWins && (
        <rect x={x} y={dividerY} width={4} height={halfBodyH} rx={2} fill={purple} />
      )}

      {/* ── Home row winner background ───────────────────────────────────── */}
      {homeWins && (
        <rect x={x + 4} y={y} width={W - 4} height={halfBodyH + timeBarH}
          rx={10} fill={purpleSoft}
        />
      )}

      {/* ── Visitor row winner background ────────────────────────────────── */}
      {visitorWins && (
        <rect x={x + 4} y={dividerY} width={W - 4} height={halfBodyH}
          rx={10} fill={purpleSoft}
        />
      )}

      {/* ── Time text ───────────────────────────────────────────────────── */}
      {game._time && (
        <text
          x={x + W / 2} y={y + 12}
          textAnchor="middle" fontSize={9} fontWeight={500}
          fill={textMuted} fontFamily={ff}
        >
          {game._time}
        </text>
      )}

      {/* ── Divider between home and visitor ─────────────────────────────── */}
      <line x1={x + 8} y1={dividerY} x2={x + W - 8} y2={dividerY}
        stroke={dividerCol} strokeWidth={1}
      />

      {/* ── Home avatar ─────────────────────────────────────────────────── */}
      <circle cx={avatarX} cy={homeRowMid} r={avatarR}
        fill={homeWins ? homeColor : homeColor + '20'}
      />
      <text x={avatarX} y={homeRowMid + 4} textAnchor="middle"
        fontSize={9} fontWeight={700}
        fill={homeWins ? '#fff' : homeColor}
        fontFamily={ff}
      >
        {(homeName === 'BYE' || homeName === 'TBD') ? '?' : homeName.charAt(0).toUpperCase()}
      </text>

      {/* ── Home team name ──────────────────────────────────────────────── */}
      <text x={nameX} y={homeRowMid + 4}
        fontSize={fs} fontWeight={homeWins ? 700 : 400}
        fill={(homeName === 'BYE' || homeName === 'TBD') ? textLight : (homeWins ? purpleDeep : textDark)}
        fontFamily={ff}
        fontStyle={(homeName === 'BYE' || homeName === 'TBD') ? 'italic' : 'normal'}
      >
        {homeName}
      </text>

      {/* ── Home score ──────────────────────────────────────────────────── */}
      {home.score && home.score.score !== undefined && (
        <text x={scoreX} y={homeRowMid + 4}
          textAnchor="end" fontSize={fsScore} fontWeight={800}
          fill={homeWins ? purple : textMuted}
          fontFamily={ff}
        >
          {home.score.score}
        </text>
      )}

      {/* ── Visitor avatar ──────────────────────────────────────────────── */}
      <circle cx={avatarX} cy={vivRowMid} r={avatarR}
        fill={visitorWins ? visitorColor : visitorColor + '20'}
      />
      <text x={avatarX} y={vivRowMid + 4} textAnchor="middle"
        fontSize={9} fontWeight={700}
        fill={visitorWins ? '#fff' : visitorColor}
        fontFamily={ff}
      >
        {(visitorName === 'BYE' || visitorName === 'TBD') ? '?' : visitorName.charAt(0).toUpperCase()}
      </text>

      {/* ── Visitor team name ───────────────────────────────────────────── */}
      <text x={nameX} y={vivRowMid + 4}
        fontSize={fs} fontWeight={visitorWins ? 700 : 400}
        fill={(visitorName === 'BYE' || visitorName === 'TBD') ? textLight : (visitorWins ? purpleDeep : textDark)}
        fontFamily={ff}
        fontStyle={(visitorName === 'BYE' || visitorName === 'TBD') ? 'italic' : 'normal'}
      >
        {visitorName}
      </text>

      {/* ── Visitor score ───────────────────────────────────────────────── */}
      {visitor.score && visitor.score.score !== undefined && (
        <text x={scoreX} y={vivRowMid + 4}
          textAnchor="end" fontSize={fsScore} fontWeight={800}
          fill={visitorWins ? purple : textMuted}
          fontFamily={ff}
        >
          {visitor.score.score}
        </text>
      )}

      {/* ── "Winner" star badge for the final match ─────────────────────── */}
      {hasWinner && game.sides.home.seed && game.sides.home.seed.sourceGame === null &&
        game.sides.visitor.seed && game.sides.visitor.seed.sourceGame === null ? null : (
        hasWinner && (
          <text x={x + W - 14} y={y + 14} fontSize={10} fill="#f59e0b" fontFamily={ff}>
            ★
          </text>
        )
      )}
    </g>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function DrawsCard(props) {
  var levels         = props.level    || [];
  var schedule       = props.schedule || [];
  var updatedFixture = props.updatedFixture;

  if (!levels.length || !schedule.length) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
        No bracket data to display.
      </div>
    );
  }

  // ── Group by level ────────────────────────────────────────────────────────
  var fixturesByLevel = {};
  levels.forEach(function(lv) { fixturesByLevel[lv] = []; });
  schedule.forEach(function(fixture, origIdx) {
    if (fixturesByLevel[fixture.match_level] !== undefined) {
      fixturesByLevel[fixture.match_level].push({ fixture: fixture, origIdx: origIdx });
    }
  });

  // ── Build tree rooted at the Final ───────────────────────────────────────
  var finalGame = buildGameTree(fixturesByLevel, levels, levels.length - 1, 0);
  if (!finalGame) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
        Could not build bracket tree.
      </div>
    );
  }

  // ── Click handler ─────────────────────────────────────────────────────────
  function handleClick(game) {
    if (game._origIdx !== undefined && updatedFixture) {
      updatedFixture(game._origIdx);
    }
  }

  // ── Wrap GameCard so handleClick is in scope ──────────────────────────────
  function BoundCard(cardProps) {
    return <GameCard onMatchClick={handleClick} {...cardProps} />;
  }

  return (
    <div style={{ overflowX: 'auto', overflowY: 'visible', padding: '8px 0 24px' }}>
      <div className="bracket-wrap">
        <Bracket
          game={finalGame}
          GameComponent={BoundCard}
          gameDimensions={{ width: 220, height: 100 }}
          roundSeparatorWidth={56}
          svgPadding={24}
          lineInfo={{ yOffset: -6, separation: 6, homeVisitorSpread: 11 }}
        />
      </div>
    </div>
  );
}


// WEBPACK FOOTER //
// ./src/app/views/partner/crud/viewKnockoutScreen/KnockoutCard.js